import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import apiClient from '../../api/client';

const CreateOpportunityDialog = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [formData, setFormData] = useState({
    program_name: '',
    oem: '',
    platform_code: '',
    stage: 'target',
    priority: 'medium',
    revenue_potential_cents: '',
    confidence_percent: '',
    sop_date: '',
    eop_date: '',
    target_award_date: '',
    region: '',
    country: '',
    plant_location: '',
    total_lifetime_volume: '',
    notes: '',
  });

  const OEM_OPTIONS = ['Ford', 'GM', 'Stellantis', 'Honda', 'Toyota', 'Tesla', 'Nissan', 'VW', 'BMW', 'Mercedes'];
  const STAGE_OPTIONS = [
    { value: 'target', label: 'Target' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'rfq', label: 'RFQ' },
    { value: 'quoted', label: 'Quoted' },
    { value: 'awarded', label: 'Awarded' },
    { value: 'sourced', label: 'Sourced' },
  ];
  const PRIORITY_OPTIONS = [
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];
  const REGION_OPTIONS = ['North America', 'Europe', 'Asia', 'South America', 'Middle East', 'Africa'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.program_name.trim()) {
      setError('Program name is required');
      return;
    }
    if (!formData.oem) {
      setError('OEM is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare data for API
      const payload = {
        program_name: formData.program_name.trim(),
        oem: formData.oem,
        platform_code: formData.platform_code || null,
        stage: formData.stage,
        priority: formData.priority,
        revenue_potential_cents: formData.revenue_potential_cents ? parseInt(formData.revenue_potential_cents) * 100 : null,
        confidence_percent: formData.confidence_percent ? parseInt(formData.confidence_percent) : null,
        sop_date: formData.sop_date || null,
        eop_date: formData.eop_date || null,
        target_award_date: formData.target_award_date || null,
        region: formData.region || null,
        country: formData.country || null,
        plant_location: formData.plant_location || null,
        total_lifetime_volume: formData.total_lifetime_volume ? parseInt(formData.total_lifetime_volume) : null,
        notes: formData.notes || null,
        revenue_source: 'manual',
      };

      const response = await apiClient.post('/api/v1/pipeline/opportunities', payload);

      if (response.data.success) {
        onSuccess && onSuccess(response.data.data);
        handleClose();
      } else {
        setError(response.data.error || 'Failed to create opportunity');
      }
    } catch (err) {
      console.error('Error creating opportunity:', err);
      setError(err.response?.data?.detail || 'Failed to create opportunity. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        program_name: '',
        oem: '',
        platform_code: '',
        stage: 'target',
        priority: 'medium',
        revenue_potential_cents: '',
        confidence_percent: '',
        sop_date: '',
        eop_date: '',
        target_award_date: '',
        region: '',
        country: '',
        plant_location: '',
        total_lifetime_volume: '',
        notes: '',
      });
      setError(null);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Create New Opportunity</h2>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">
              {error}
            </div>
          )}

          {/* Core Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Core Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Program Name */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Program Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="program_name"
                  value={formData.program_name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Ford F-150 Gen 15"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* OEM */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  OEM <span className="text-red-500">*</span>
                </label>
                <select
                  name="oem"
                  value={formData.oem}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select OEM</option>
                  {OEM_OPTIONS.map(oem => (
                    <option key={oem} value={oem}>{oem}</option>
                  ))}
                </select>
              </div>

              {/* Platform Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Platform Code
                </label>
                <input
                  type="text"
                  name="platform_code"
                  value={formData.platform_code}
                  onChange={handleChange}
                  placeholder="e.g., P552"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Stage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stage
                </label>
                <select
                  name="stage"
                  value={formData.stage}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {STAGE_OPTIONS.map(stage => (
                    <option key={stage.value} value={stage.value}>{stage.label}</option>
                  ))}
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Priority
                </label>
                <select
                  name="priority"
                  value={formData.priority}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {PRIORITY_OPTIONS.map(priority => (
                    <option key={priority.value} value={priority.value}>{priority.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Financial & Volume */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Financial & Volume</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Revenue Potential */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Revenue Potential ($)
                </label>
                <input
                  type="number"
                  name="revenue_potential_cents"
                  value={formData.revenue_potential_cents}
                  onChange={handleChange}
                  placeholder="e.g., 5000000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Annual revenue in dollars</p>
              </div>

              {/* Lifetime Volume */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lifetime Volume
                </label>
                <input
                  type="number"
                  name="total_lifetime_volume"
                  value={formData.total_lifetime_volume}
                  onChange={handleChange}
                  placeholder="e.g., 150000"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Total units</p>
              </div>

              {/* Confidence */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confidence (%)
                </label>
                <input
                  type="number"
                  name="confidence_percent"
                  value={formData.confidence_percent}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  placeholder="e.g., 70"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Timeline</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* SOP Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SOP Date
                </label>
                <input
                  type="date"
                  name="sop_date"
                  value={formData.sop_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* EOP Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  EOP Date
                </label>
                <input
                  type="date"
                  name="eop_date"
                  value={formData.eop_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Target Award Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Award Date
                </label>
                <input
                  type="date"
                  name="target_award_date"
                  value={formData.target_award_date}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Location</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Region */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Region
                </label>
                <select
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Region</option>
                  {REGION_OPTIONS.map(region => (
                    <option key={region} value={region}>{region}</option>
                  ))}
                </select>
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  placeholder="e.g., United States"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Plant Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plant Location
                </label>
                <input
                  type="text"
                  name="plant_location"
                  value={formData.plant_location}
                  onChange={handleChange}
                  placeholder="e.g., Dearborn, MI"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Additional Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="3"
                placeholder="Add any additional notes or context..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{loading ? 'Creating...' : 'Create Opportunity'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOpportunityDialog;
